import { onUnmounted, ref } from 'vue'

export function useLoadImage() {
  const isLoading = ref(false)
  let abortController = null

  function createAbortController() {
    // Cancel any existing request before creating a new one
    if (abortController) {
      abortController.abort()
    }
    abortController = new AbortController()
    return abortController.signal
  }

  async function loadImage({ action }) {
    try {
      isLoading.value = true
      const signal = createAbortController()

      // Pass the signal to your action if it supports it
      const image = await action(signal)

      if (image?.fileContents) {
        const contentType = image.contentType
        const asBase64 = image.fileContents

        const byteCharacters = atob(asBase64)
        const byteNumbers = Array.from({ length: byteCharacters.length })

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: contentType })
        const created = URL.createObjectURL(blob)

        return created
      }
    } catch (e) {
      // Don't log abort errors as they are expected when cancelling
      if (e.name !== 'AbortError') {
        console.error('loadImage error', e)
      }
      return null
    } finally {
      isLoading.value = false
    }
  }

  function cancelRequest() {
    if (abortController) {
      abortController.abort()
      abortController = null
    }
  }

  // Automatically cancel any pending request when component unmounts
  onUnmounted(() => {
    cancelRequest()
  })

  return {
    loadImage,
    isLoading,
    cancelRequest, // Exposing this allows manual cancellation too
  }
}
